import { Form, message, Spin } from "antd";
import { AiProfileForm } from "./components/AiProfileForm";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  updateAiProfile,
  fetchAiProfile,
  AiProfileInput,
} from "@/lib/api/resources/agents";

export default function AiProfileEdit() {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const fromSearch = location.state?.fromSearch || "";

  const { data: profile, isLoading } = useQuery({
    queryKey: ["ai_profile", id],
    queryFn: () => fetchAiProfile(id!),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: (values: Partial<AiProfileInput>) =>
      updateAiProfile(id!, values),
    onSuccess: () => {
      message.success("AI Profile updated successfully");
      queryClient.invalidateQueries({ queryKey: ["ai_profiles"] });
      queryClient.invalidateQueries({ queryKey: ["ai_profile", id] });
      navigate(`/ai-profiles${fromSearch}`);
    },
    onError: () => {
      message.error("Failed to update profile");
    },
  });

  const onFinish = (payload: AiProfileInput) => {
    mutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-2 pb-12">
      <div className="mb-8 pl-1">
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
          Edit AI Provider Profile
        </h1>
        <p className="text-gray-500 mt-2 text-sm max-w-2xl">
          Modify the configuration for this AI Provider connection.
        </p>
      </div>
      <AiProfileForm
        form={form}
        onFinish={onFinish}
        initialValues={{
          name: profile?.name,
          // The API key is never returned on read; pass the read-safe aiConnection so
          // the form can pre-fill the non-secret fields (provider/model/host/options). The API
          // key is intentionally left blank and must be re-entered to save.
          aiConnection: profile?.aiConnection,
          capabilities: profile?.capabilities,
          contextWindowInput: profile?.contextWindowInput,
          contextWindowOutput: profile?.contextWindowOutput,
        }}
        profileId={id}
        isSubmitting={mutation.isPending}
      />
    </div>
  );
}
